import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  Truck, Package, Plus, Pencil, Trash2, X, Save, Loader2,
  ArrowLeft, RefreshCw, Printer, Search, MapPin, User,
  DollarSign, Receipt, CheckSquare,
} from "lucide-react";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";

const ESTADO_LABEL = {
  pendiente: "Pendiente",
  en_transito: "En Tránsito",
  entregado: "Entregado",
  facturado: "Facturado",
  cancelado: "Cancelado",
};
const ESTADO_COLOR = {
  pendiente: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  en_transito: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  entregado: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  facturado: "text-violet-400 bg-violet-400/10 border-violet-400/20",
  cancelado: "text-slate-400 bg-slate-400/10 border-slate-400/20",
};
const fmtNum = (n, moneda = "PYG") => {
  const v = Number(n) || 0;
  if (moneda === "PYG") return `₲ ${Math.round(v).toLocaleString("es-PY")}`;
  return `${moneda} ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ── Buscador simple de entidad (cliente o chofer) ────────────────────────────
function EntidadSearchSelect({ entidades, value, onSelect, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return (entidades || []).slice(0, 12);
    const q = query.toLowerCase();
    return (entidades || []).filter(e => (e.nombre || "").toLowerCase().includes(q) || (e.ruc || "").toLowerCase().includes(q)).slice(0, 12);
  }, [entidades, query]);

  const seleccionado = (entidades || []).find(e => e.id === value);

  if (seleccionado) {
    return (
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
        <span className="text-sm text-white flex-1 truncate">{seleccionado.nombre}</span>
        <button type="button" onClick={() => onSelect(null)} className="text-slate-500 hover:text-white">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          className={`${INPUT} pl-8`}
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-56 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">Sin resultados</div>
          )}
          {filtered.map(e => (
            <button
              type="button"
              key={e.id}
              onClick={() => { onSelect(e.id); setQuery(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
            >
              {e.nombre}{e.ruc ? ` · ${e.ruc}` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TransportePage() {
  const navigate = useNavigate();
  const { token, hasPermission, activeEmpresaPropia, user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const slug = activeEmpresaPropia?.slug || "";
  const logoQs = slug ? `?logo_tipo=${encodeURIComponent(slug)}` : "";
  const logoQsAnd = slug ? `&logo_tipo=${encodeURIComponent(slug)}` : "";

  const [categoriasGasto, setCategoriasGasto] = useState([]);
  useEffect(() => {
    fetch(`${API}/admin/categorias-gastos${logoQs}`, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setCategoriasGasto((Array.isArray(d) ? d : []).map((c) => c.nombre).filter(Boolean)))
      .catch(() => setCategoriasGasto([]));
  }, [slug]); // eslint-disable-line

  const isAdmin = user?.role === "admin" || user?.role === "super_admin" || user?.role === "gerente";
  const canVer = isAdmin || hasPermission("transporte.ver");
  const canCrear = isAdmin || hasPermission("transporte.crear");
  const canEditar = isAdmin || hasPermission("transporte.editar");
  const canEliminar = isAdmin || hasPermission("transporte.eliminar");
  const canCambiarEstado = isAdmin || hasPermission("transporte.cambiar_estado");
  const canImprimir = isAdmin || hasPermission("transporte.imprimir_orden");
  const canLiquidar = isAdmin || hasPermission("transporte.liquidar");

  const [tab, setTab] = useState("ordenes");
  const [vehiculos, setVehiculos] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [modalVehiculo, setModalVehiculo] = useState(null); // null | {} | doc
  const [modalOrden, setModalOrden] = useState(null);
  const [seleccion, setSeleccion] = useState([]); // ids de órdenes marcadas
  const [saving, setSaving] = useState(false);
  const [precioKiloEmpresa, setPrecioKiloEmpresa] = useState("");
  const [toleranciaEmpresa, setToleranciaEmpresa] = useState("");

  const emptyVehiculo = () => ({ placa: "", marca: "", modelo: "", capacidad_carga: "", chofer_habitual_id: null, notas: "", activo: true });
  const emptyOrden = () => ({ cliente_id: null, origen: "", destino: "", fecha_carga: new Date().toISOString().slice(0, 10), vehiculo_id: null, chofer_id: null, descripcion_carga: "", monto_flete: "", moneda: "PYG", observaciones: "", peso_salida: "", peso_llegada: "", nro_remision: "", gastos_chofer: "", gastos_descripcion: "", gastos: [], precio_kilo: "", peso_tolerancia: "" });
  const [vehiculoForm, setVehiculoForm] = useState(emptyVehiculo());
  const [ordenForm, setOrdenForm] = useState(emptyOrden());

  const fetchAll = useCallback(async () => {
    if (!canVer) { setLoading(false); return; }
    try {
      const [rVeh, rOrd, rCli, rCho, rCta, rCfg] = await Promise.all([
        fetch(`${API}/admin/transporte/vehiculos${logoQs}`, { headers }),
        fetch(`${API}/admin/transporte/ordenes${logoQs}`, { headers }),
        fetch(`${API}/admin/entidades?es_cliente=true${logoQsAnd}`, { headers }),
        fetch(`${API}/admin/entidades?es_chofer=true${logoQsAnd}`, { headers }),
        fetch(`${API}/admin/cuentas-bancarias${logoQs}`, { headers }),
        fetch(`${API}/admin/transporte/config${logoQs}`, { headers }),
      ]);
      if (rCfg.ok) { const c = await rCfg.json(); setPrecioKiloEmpresa(c.precio_kilo ? String(c.precio_kilo) : ""); setToleranciaEmpresa(c.peso_tolerancia ? String(c.peso_tolerancia) : ""); }
      if (rVeh.ok) setVehiculos(await rVeh.json());
      if (rOrd.ok) setOrdenes(await rOrd.json());
      if (rCli.ok) setClientes(await rCli.json());
      if (rCho.ok) setChoferes(await rCho.json());
      if (rCta.ok) setCuentas(await rCta.json());
    } catch { toast.error("Error al cargar Transporte"); }
    setLoading(false);
    setRefreshing(false);
  }, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRefresh = () => { setRefreshing(true); fetchAll(); };

  // ── Vehículos ──────────────────────────────────────────────────────────────
  const openVehiculo = (v) => { setVehiculoForm(v ? { ...v } : emptyVehiculo()); setModalVehiculo(v || {}); };

  const guardarVehiculo = async () => {
    if (!vehiculoForm.placa?.trim()) { toast.error("La placa es obligatoria"); return; }
    setSaving(true);
    try {
      const esEdicion = !!modalVehiculo?.id;
      const url = esEdicion ? `${API}/admin/transporte/vehiculos/${modalVehiculo.id}` : `${API}/admin/transporte/vehiculos${logoQs}`;
      const res = await fetch(url, {
        method: esEdicion ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(vehiculoForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      toast.success(esEdicion ? "Vehículo actualizado" : "Vehículo creado");
      setModalVehiculo(null);
      fetchAll();
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const eliminarVehiculo = async (v) => {
    if (!window.confirm(`¿Eliminar el vehículo ${v.placa}?`)) return;
    try {
      const res = await fetch(`${API}/admin/transporte/vehiculos/${v.id}`, { method: "DELETE", headers });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al eliminar"); }
      toast.success("Vehículo eliminado");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Órdenes de Carga ─────────────────────────────────────────────────────────
  const openOrden = async (o) => {
    // Siempre toma el precio/tolerancia vigentes de Configuración (si la orden no tiene propios)
    let pk = precioKiloEmpresa, tol = toleranciaEmpresa;
    try {
      const r = await fetch(`${API}/admin/transporte/config${logoQs}`, { headers });
      if (r.ok) { const c = await r.json(); pk = c.precio_kilo ? String(c.precio_kilo) : ""; tol = c.peso_tolerancia ? String(c.peso_tolerancia) : ""; setPrecioKiloEmpresa(pk); setToleranciaEmpresa(tol); }
    } catch { /* usa lo ya cargado */ }
    setOrdenForm(o
      ? { ...o, precio_kilo: o.precio_kilo || pk || "", peso_tolerancia: o.peso_tolerancia ?? (tol || ""),
          gastos: (o.gastos && o.gastos.length) ? o.gastos.map((g) => ({ ...g }))
            : (Number(o.gastos_chofer) > 0 ? [{ categoria: "", descripcion: o.gastos_descripcion || "", monto: o.gastos_chofer }] : []) }
      : { ...emptyOrden(), precio_kilo: pk || "", peso_tolerancia: tol || "" });
    setModalOrden(o || {});
  };

  const guardarConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/transporte/config${logoQs}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ precio_kilo: Number(precioKiloEmpresa) || 0, peso_tolerancia: Number(toleranciaEmpresa) || 0 }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(typeof e.detail === "string" ? e.detail : "Error al guardar"); }
      toast.success("Configuración guardada");
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const guardarOrden = async () => {
    if (!ordenForm.origen?.trim() || !ordenForm.destino?.trim()) { toast.error("Origen y destino son obligatorios"); return; }
    setSaving(true);
    try {
      const esEdicion = !!modalOrden?.id;
      const url = esEdicion ? `${API}/admin/transporte/ordenes/${modalOrden.id}` : `${API}/admin/transporte/ordenes${logoQs}`;
      const gastosLineas = (ordenForm.gastos || []).filter((g) => Number(g.monto) > 0).map((g) => ({ categoria: g.categoria || null, descripcion: g.descripcion || null, monto: Number(g.monto) }));
      const payload = { ...ordenForm, gastos: gastosLineas, gastos_chofer: gastosLineas.reduce((a, g) => a + g.monto, 0), monto_flete: Number(ordenForm.monto_flete) || 0, precio_kilo: ordenForm.precio_kilo === "" || ordenForm.precio_kilo == null ? null : Number(ordenForm.precio_kilo), peso_tolerancia: ordenForm.peso_tolerancia === "" || ordenForm.peso_tolerancia == null ? null : Number(ordenForm.peso_tolerancia) };
      const res = await fetch(url, {
        method: esEdicion ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      toast.success(esEdicion ? "Orden de carga actualizada" : "Orden de carga creada");
      setModalOrden(null);
      fetchAll();
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const cambiarEstado = async (orden, estado) => {
    try {
      const res = await fetch(`${API}/admin/transporte/ordenes/${orden.id}/estado`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ estado }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al cambiar estado"); }
      toast.success(`Estado actualizado a ${ESTADO_LABEL[estado] || estado}`);
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Selección múltiple + liquidación ────────────────────────────────────────
  const toggleSel = (id) => setSeleccion(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  const todasSel = ordenes.length > 0 && seleccion.length === ordenes.length;
  const toggleTodas = () => setSeleccion(todasSel ? [] : ordenes.map(o => o.id));
  const seleccionadas = ordenes.filter(o => seleccion.includes(o.id));

  // Liquidar al chofer = emitir una Compra de servicios a pagar (módulo Compras),
  // con un ítem por orden por el monto de la comisión del chofer.
  const liquidarChofer = (lista) => {
    if (!lista.length) { toast.error("Seleccioná al menos una orden"); return; }
    if (lista.some(o => !o.chofer_id)) { toast.error("Hay órdenes sin chofer asignado"); return; }
    if (lista.some(o => o.chofer_liquidado)) { toast.error("Hay órdenes ya liquidadas al chofer"); return; }
    if (new Set(lista.map(o => o.chofer_id)).size > 1) { toast.error("Las órdenes deben ser del mismo chofer"); return; }
    if (new Set(lista.map(o => o.moneda || "PYG")).size > 1) { toast.error("Las órdenes deben estar en la misma moneda"); return; }
    if (lista.every(o => !(o.chofer_comision_sugerida > 0))) { toast.error("El chofer no tiene comisión configurada (Catastro → Chofer)"); return; }
    navigate(`/sistema/egresos?nuevo=compra_transporte&orden_ids=${lista.map(o => o.id).join(",")}`);
  };

  // Liquidación al cliente: Factura precargada con un concepto por orden.
  const facturarOrdenes = (lista) => {
    if (!lista.length) { toast.error("Seleccioná al menos una orden"); return; }
    if (lista.some(o => !o.cliente_id)) { toast.error("Hay órdenes sin cliente asignado"); return; }
    if (lista.some(o => o.factura_id)) { toast.error("Hay órdenes ya facturadas"); return; }
    if (new Set(lista.map(o => o.cliente_id)).size > 1) { toast.error("Las órdenes deben ser del mismo cliente"); return; }
    if (new Set(lista.map(o => o.moneda || "PYG")).size > 1) { toast.error("Las órdenes deben estar en la misma moneda"); return; }
    navigate(`/sistema/ventas?nuevo=factura_transporte&orden_carga_ids=${lista.map(o => o.id).join(",")}`);
  };

  const eliminarOrden = async (o) => {
    if (!window.confirm(`¿Eliminar la orden de carga ${o.numero}?`)) return;
    try {
      const res = await fetch(`${API}/admin/transporte/ordenes/${o.id}`, { method: "DELETE", headers });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al eliminar"); }
      toast.success("Orden de carga eliminada");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const imprimirOrden = (o) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const emisorNombre = activeEmpresaPropia?.nombre || activeEmpresaPropia?.nombre_fantasia || "";
    const emisorRuc = activeEmpresaPropia?.ruc || "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Orden de Carga ${o.numero}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;color:#111827;padding:24px;font-size:12px}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #2563eb;padding-bottom:10px;margin-bottom:14px}
      .header h1{font-size:15px;margin:0;color:#1e3a8a}
      .header .doc{border:2px solid #2563eb;border-radius:6px;padding:8px 14px;text-align:center}
      .doc .num{font-size:16px;font-weight:800;color:#1e3a8a}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px}
      .box{border:1px solid #d1d5db;border-radius:6px;padding:8px 10px}
      .box .lbl{font-size:9px;color:#6b7280;text-transform:uppercase;font-weight:700;margin-bottom:2px}
      .box .val{font-size:12px;font-weight:600}
      .full{grid-column:1 / -1}
      table{width:100%;border-collapse:collapse;margin-bottom:16px}
      td,th{border:1px solid #d1d5db;padding:6px 8px;font-size:11px;text-align:left}
      th{background:#2563eb;color:#fff;font-size:9px;text-transform:uppercase}
      .firmas{display:flex;gap:20px;margin-top:40px}
      .firma{flex:1;text-align:center}
      .firma .linea{border-top:1px solid #111;margin-bottom:6px;padding-top:36px}
      .firma .tit{font-size:9px;font-weight:700;text-transform:uppercase;color:#374151}
      @media print{ .no-print{display:none} }
    </style></head><body>
      <div class="no-print" style="text-align:right;margin-bottom:10px">
        <button onclick="window.print()" style="padding:8px 16px;background:#2563eb;color:#fff;border:none;border-radius:6px;cursor:pointer">🖨 Imprimir</button>
      </div>
      <div class="header">
        <div>
          <h1>${emisorNombre}</h1>
          ${emisorRuc ? `<div style="font-size:10px;color:#6b7280">RUC: ${emisorRuc}</div>` : ""}
        </div>
        <div class="doc">
          <div style="font-size:10px;color:#6b7280">ORDEN DE CARGA</div>
          <div class="num">${o.numero}</div>
          <div style="font-size:9px;color:#9ca3af">Comprobante interno · no fiscal</div>
        </div>
      </div>
      <div class="grid">
        <div class="box"><div class="lbl">Cliente</div><div class="val">${o.cliente_nombre || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Fecha de Carga</div><div class="val">${o.fecha_carga || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">${o.cliente_tipo_documento === "CI" ? "C.I." : "RUC / C.I."} del Cliente</div><div class="val">${o.cliente_documento || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Teléfono del Cliente</div><div class="val">${o.cliente_telefono || "&nbsp;"}</div></div>
        <div class="box full"><div class="lbl">Dirección del Cliente</div><div class="val">${o.cliente_direccion || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Origen</div><div class="val">${o.origen || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Destino</div><div class="val">${o.destino || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Vehículo</div><div class="val">${o.vehiculo_placa || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Chofer</div><div class="val">${o.chofer_nombre || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">C.I. del Chofer</div><div class="val">${o.chofer_documento || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Teléfono del Chofer</div><div class="val">${o.chofer_telefono || "&nbsp;"}</div></div>
        <div class="box"><div class="lbl">Dirección del Chofer</div><div class="val">${o.chofer_direccion || "&nbsp;"}</div></div>
        <div class="box full"><div class="lbl">Descripción de la Carga</div><div class="val">${o.descripcion_carga || "&nbsp;"}</div></div>
        ${o.nro_remision ? `<div class="box"><div class="lbl">N° de Remisión</div><div class="val">${o.nro_remision}</div></div>` : ""}
        ${o.observaciones ? `<div class="box full"><div class="lbl">Observaciones</div><div class="val">${o.observaciones}</div></div>` : ""}
      </div>
      <div class="firmas">
        <div class="firma"><div class="linea"></div><div class="tit">Remitente</div></div>
        <div class="firma"><div class="linea"></div><div class="tit">Transportista</div></div>
        <div class="firma"><div class="linea"></div><div class="tit">Destinatario</div></div>
      </div>
    </body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch {} }, 300);
  };

  if (!canVer) {
    return (
      <div className="min-h-screen bg-arandu-dark text-white flex items-center justify-center p-6">
        <div className="text-center">
          <Truck className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No tenés permiso para ver Transporte.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-arandu-dark text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-arandu-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-1 -ml-1 rounded-lg hover:bg-white/5 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Truck className="w-6 h-6 text-emerald-400" />
            <div>
              <h1 className="text-lg font-bold text-white">Transporte</h1>
              <p className="text-xs text-slate-400">{activeEmpresaPropia?.nombre || ""} — Control de flete y órdenes de carga</p>
            </div>
          </div>
          <button onClick={handleRefresh} disabled={refreshing} className={BTN_SEC}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-4 border-b border-white/10">
        {[
          { key: "ordenes", label: "Órdenes de Carga", icon: <Package className="w-4 h-4" /> },
          { key: "vehiculos", label: "Vehículos", icon: <Truck className="w-4 h-4" /> },
          ...(canEditar ? [{ key: "config", label: "Configuración", icon: <Save className="w-4 h-4" /> }] : []),
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? "text-white border-blue-500 bg-blue-500/10" : "text-slate-400 border-transparent hover:text-white hover:border-white/20"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6">
        {tab === "ordenes" && (
          <div>
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                {seleccion.length > 0 && (
                  <>
                    <span className="text-xs text-slate-400 flex items-center gap-1"><CheckSquare className="w-4 h-4" />{seleccion.length} seleccionada(s)</span>
                    {canLiquidar && (
                      <button onClick={() => liquidarChofer(seleccionadas)} className={BTN_SEC}>
                        <DollarSign className="w-4 h-4 text-emerald-400" /> Liquidar al chofer
                      </button>
                    )}
                    {(isAdmin || hasPermission("facturas.crear")) && (
                      <button onClick={() => facturarOrdenes(seleccionadas)} className={BTN_SEC}>
                        <Receipt className="w-4 h-4 text-violet-400" /> Facturar al cliente
                      </button>
                    )}
                    <button onClick={() => setSeleccion([])} className="text-xs text-slate-500 hover:text-white">Limpiar</button>
                  </>
                )}
              </div>
              {canCrear && (
                <button onClick={() => openOrden(null)} className={BTN_PRI}>
                  <Plus className="w-4 h-4" /> Nueva Orden de Carga
                </button>
              )}
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 w-8"><input type="checkbox" checked={todasSel} onChange={toggleTodas} title="Seleccionar todas" /></th>
                    <th className="text-left px-4 py-2">N°</th>
                    <th className="text-left px-4 py-2">Fecha</th>
                    <th className="text-left px-4 py-2">Cliente</th>
                    <th className="text-left px-4 py-2">Origen → Destino</th>
                    <th className="text-left px-4 py-2">Vehículo / Chofer</th>
                    <th className="text-left px-4 py-2">Remisión</th>
                    <th className="text-right px-4 py-2">Flete</th>
                    <th className="text-right px-4 py-2">Saldo líquido</th>
                    <th className="text-left px-4 py-2">Liquidación</th>
                    <th className="text-left px-4 py-2">Estado</th>
                    <th className="text-right px-4 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {ordenes.length === 0 && (
                    <tr><td colSpan={12} className="text-center py-8 text-slate-500">Sin órdenes de carga registradas</td></tr>
                  )}
                  {ordenes.map(o => (
                    <tr key={o.id} className="hover:bg-white/[0.02]">
                      <td className="px-3 py-2"><input type="checkbox" checked={seleccion.includes(o.id)} onChange={() => toggleSel(o.id)} /></td>
                      <td className="px-4 py-2 font-medium">{o.numero}</td>
                      <td className="px-4 py-2 text-slate-400">{o.fecha_carga}</td>
                      <td className="px-4 py-2">{o.cliente_nombre || "—"}</td>
                      <td className="px-4 py-2 text-slate-300">
                        <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-500" />{o.origen} → {o.destino}</div>
                      </td>
                      <td className="px-4 py-2 text-slate-400">
                        {o.vehiculo_placa || "—"}{o.chofer_nombre ? ` · ${o.chofer_nombre}` : ""}
                      </td>
                      <td className="px-4 py-2 text-slate-400">{o.nro_remision || "—"}</td>
                      <td className="px-4 py-2 text-right">{fmtNum(o.monto_flete, o.moneda)}</td>
                      <td className="px-4 py-2 text-right text-emerald-400 font-medium">{fmtNum(o.saldo_liquido ?? o.monto_flete, o.moneda)}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col gap-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border w-fit ${o.chofer_liquidado ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" : "text-amber-400 bg-amber-400/10 border-amber-400/20"}`}>
                            {o.chofer_liquidado ? `Chofer liquidado (${fmtNum(o.chofer_monto_liquidado, o.moneda)})` : "Chofer pendiente"}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border w-fit ${o.factura_id ? "text-violet-400 bg-violet-400/10 border-violet-400/20" : "text-slate-400 bg-slate-400/10 border-slate-400/20"}`}>
                            {o.factura_id ? `Facturado ${o.factura_numero || ""}` : "Sin facturar"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {canCambiarEstado ? (
                          <select
                            value={o.estado}
                            onChange={(e) => cambiarEstado(o, e.target.value)}
                            className={`text-xs rounded-lg border px-2 py-1 bg-transparent ${ESTADO_COLOR[o.estado] || ""}`}
                          >
                            {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k} className="bg-arandu-dark text-white">{v}</option>)}
                          </select>
                        ) : (
                          <span className={`text-xs px-2 py-1 rounded-lg border ${ESTADO_COLOR[o.estado] || ""}`}>{ESTADO_LABEL[o.estado] || o.estado}</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          {canLiquidar && !o.chofer_liquidado && o.chofer_id && (
                            <button onClick={() => liquidarChofer([o])} className="p-1.5 text-slate-400 hover:text-emerald-400 rounded-lg hover:bg-white/10" title="Liquidar al chofer (Compra de servicios)">
                              <DollarSign className="w-4 h-4" />
                            </button>
                          )}
                          {!o.factura_id && (isAdmin || hasPermission("facturas.crear")) && (
                            <button onClick={() => facturarOrdenes([o])} className="p-1.5 text-slate-400 hover:text-violet-400 rounded-lg hover:bg-white/10" title="Liquidación al cliente (Facturar)">
                              <Receipt className="w-4 h-4" />
                            </button>
                          )}
                          {canImprimir && (
                            <button onClick={() => imprimirOrden(o)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10" title="Imprimir Orden de Carga">
                              <Printer className="w-4 h-4" />
                            </button>
                          )}
                          {canEditar && (
                            <button onClick={() => openOrden(o)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10" title="Editar">
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {canEliminar && (
                            <button onClick={() => eliminarOrden(o)} className="p-1.5 text-slate-400 hover:text-rose-400 rounded-lg hover:bg-white/10" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "config" && canEditar && (
          <div className="max-w-md space-y-3">
            <div>
              <label className={LABEL}>Precio por kilo (₲) — para valorizar el faltante de carga</label>
              <input type="number" min="0" className={INPUT} value={precioKiloEmpresa} onChange={(e) => setPrecioKiloEmpresa(e.target.value)} placeholder="Ej: 2500" />
              <p className="text-[11px] text-slate-500 mt-1">Se aplica a las órdenes nuevas (editable en cada orden). Faltante = (peso salida − peso llegada) × precio por kilo; se descuenta de la liquidación al chofer.</p>
            </div>
            <div>
              <label className={LABEL}>Tolerancia de peso (kg) — el faltante solo se cobra sobre lo que supera este valor</label>
              <input type="number" min="0" className={INPUT} value={toleranciaEmpresa} onChange={(e) => setToleranciaEmpresa(e.target.value)} placeholder="Ej: 30" />
              <p className="text-[11px] text-slate-500 mt-1">Ej.: tolerancia 30 kg y diferencia 50 kg → se cobran 20 kg. Con diferencia menor o igual a la tolerancia, no se cobra faltante.</p>
            </div>
            <button onClick={guardarConfig} disabled={saving} className={BTN_PRI}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        )}

        {tab === "vehiculos" && (
          <div>
            <div className="flex justify-end mb-4">
              {canCrear && (
                <button onClick={() => openVehiculo(null)} className={BTN_PRI}>
                  <Plus className="w-4 h-4" /> Nuevo Vehículo
                </button>
              )}
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Placa</th>
                    <th className="text-left px-4 py-2">Marca / Modelo</th>
                    <th className="text-left px-4 py-2">Capacidad</th>
                    <th className="text-left px-4 py-2">Chofer Habitual</th>
                    <th className="text-left px-4 py-2">Estado</th>
                    <th className="text-right px-4 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {vehiculos.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-500">Sin vehículos registrados</td></tr>
                  )}
                  {vehiculos.map(v => (
                    <tr key={v.id} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2 font-medium">{v.placa}</td>
                      <td className="px-4 py-2 text-slate-400">{[v.marca, v.modelo].filter(Boolean).join(" ") || "—"}</td>
                      <td className="px-4 py-2 text-slate-400">{v.capacidad_carga || "—"}</td>
                      <td className="px-4 py-2 text-slate-400">
                        {choferes.find(c => c.id === v.chofer_habitual_id)?.nombre || "—"}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-1 rounded-lg border ${v.activo !== false ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" : "text-slate-400 bg-slate-400/10 border-slate-400/20"}`}>
                          {v.activo !== false ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          {canEditar && (
                            <button onClick={() => openVehiculo(v)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10" title="Editar">
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {canEliminar && (
                            <button onClick={() => eliminarVehiculo(v)} className="p-1.5 text-slate-400 hover:text-rose-400 rounded-lg hover:bg-white/10" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Vehículo */}
      {modalVehiculo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">{modalVehiculo?.id ? "Editar Vehículo" : "Nuevo Vehículo"}</h2>
              <button onClick={() => setModalVehiculo(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={LABEL}>Placa *</label>
                <input className={INPUT} value={vehiculoForm.placa} onChange={(e) => setVehiculoForm({ ...vehiculoForm, placa: e.target.value.toUpperCase() })} placeholder="ABC 123" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Marca</label>
                  <input className={INPUT} value={vehiculoForm.marca || ""} onChange={(e) => setVehiculoForm({ ...vehiculoForm, marca: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Modelo</label>
                  <input className={INPUT} value={vehiculoForm.modelo || ""} onChange={(e) => setVehiculoForm({ ...vehiculoForm, modelo: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={LABEL}>Capacidad de Carga</label>
                <input className={INPUT} value={vehiculoForm.capacidad_carga || ""} onChange={(e) => setVehiculoForm({ ...vehiculoForm, capacidad_carga: e.target.value })} placeholder="Ej: 10 ton, 20 m³" />
              </div>
              <div>
                <label className={LABEL}>Chofer Habitual</label>
                <EntidadSearchSelect entidades={choferes} value={vehiculoForm.chofer_habitual_id} onSelect={(id) => setVehiculoForm({ ...vehiculoForm, chofer_habitual_id: id })} placeholder="Buscar chofer..." />
              </div>
              <div>
                <label className={LABEL}>Notas</label>
                <textarea className={INPUT} rows={2} value={vehiculoForm.notas || ""} onChange={(e) => setVehiculoForm({ ...vehiculoForm, notas: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={vehiculoForm.activo !== false} onChange={(e) => setVehiculoForm({ ...vehiculoForm, activo: e.target.checked })} />
                Activo
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setModalVehiculo(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={guardarVehiculo} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Orden de Carga */}
      {modalOrden && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-arandu-dark border border-white/10 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">{modalOrden?.id ? `Editar Orden ${modalOrden.numero}` : "Nueva Orden de Carga"}</h2>
              <button onClick={() => setModalOrden(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={LABEL}>Cliente</label>
                <EntidadSearchSelect entidades={clientes} value={ordenForm.cliente_id} onSelect={(id) => setOrdenForm({ ...ordenForm, cliente_id: id })} placeholder="Buscar cliente..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Origen *</label>
                  <input className={INPUT} value={ordenForm.origen} onChange={(e) => setOrdenForm({ ...ordenForm, origen: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Destino *</label>
                  <input className={INPUT} value={ordenForm.destino} onChange={(e) => setOrdenForm({ ...ordenForm, destino: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={LABEL}>Fecha de Carga</label>
                <DateInput className={INPUT} value={ordenForm.fecha_carga} onChange={(e) => setOrdenForm({ ...ordenForm, fecha_carga: e.target.value })} />
              </div>
              <div>
                <label className={LABEL}>Vehículo</label>
                <select className={INPUT} value={ordenForm.vehiculo_id || ""} onChange={(e) => setOrdenForm({ ...ordenForm, vehiculo_id: e.target.value || null })}>
                  <option value="">— Sin asignar —</option>
                  {vehiculos.filter(v => v.activo !== false).map(v => <option key={v.id} value={v.id}>{v.placa}{v.marca ? ` · ${v.marca}` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>Chofer</label>
                <EntidadSearchSelect entidades={choferes} value={ordenForm.chofer_id} onSelect={(id) => setOrdenForm({ ...ordenForm, chofer_id: id })} placeholder="Buscar chofer..." />
                {ordenForm.chofer_id && (() => {
                  const ch = choferes.find(c => c.id === ordenForm.chofer_id);
                  if (!ch) return null;
                  const comisionTxt = ch.tipo_comision_chofer === "pct"
                    ? `${ch.valor_comision_chofer || 0}% sobre el flete`
                    : ch.tipo_comision_chofer === "monto"
                      ? fmtNum(ch.valor_comision_chofer || 0)
                      : "Sin configurar";
                  return (
                    <div className="mt-1.5 text-xs text-slate-400 bg-white/5 rounded-lg px-3 py-2 space-y-0.5">
                      <div>Licencia: <span className="text-slate-300">{ch.nro_licencia || "Sin cargar (Catastro → editar chofer)"}</span>{ch.vencimiento_licencia ? ` · Vence: ${ch.vencimiento_licencia}` : ""}</div>
                      <div>Comisión: <span className="text-slate-300">{comisionTxt}</span></div>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className={LABEL}>Descripción de la Carga</label>
                <textarea className={INPUT} rows={2} value={ordenForm.descripcion_carga || ""} onChange={(e) => setOrdenForm({ ...ordenForm, descripcion_carga: e.target.value })} />
              </div>
              <div>
                <label className={LABEL}>N° de Remisión</label>
                <input className={INPUT} value={ordenForm.nro_remision || ""} onChange={(e) => setOrdenForm({ ...ordenForm, nro_remision: e.target.value })} placeholder="Documento que porta el chofer" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Peso Salida (kg)</label>
                  <input type="number" className={INPUT} value={ordenForm.peso_salida ?? ""} onChange={(e) => setOrdenForm({ ...ordenForm, peso_salida: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Peso Llegada (kg)</label>
                  <input type="number" className={INPUT} value={ordenForm.peso_llegada ?? ""} onChange={(e) => setOrdenForm({ ...ordenForm, peso_llegada: e.target.value })} />
                </div>
              </div>
              {(() => {
                const sal = ordenForm.peso_salida; const lle = ordenForm.peso_llegada;
                if (sal === "" || sal == null || lle === "" || lle == null) return null;
                const dif = Number(sal) - Number(lle);
                const pk = Number(ordenForm.precio_kilo) || 0;
                const tol = Number(ordenForm.peso_tolerancia) || 0;
                const exc = Math.max(dif - tol, 0);
                const falt = exc > 0 ? Math.round(exc * pk) : 0;
                return (
                  <div className="text-xs bg-white/5 rounded-lg px-3 py-2 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Diferencia de peso (salida − llegada)</span>
                      <span className={dif > 0 ? "text-rose-400 font-medium" : "text-slate-200 font-medium"}>{dif.toLocaleString("es-PY", { maximumFractionDigits: 3 })} kg</span>
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-slate-400">Precio por kilo (₲)</span>
                      <input type="number" min="0" className={`${INPUT} !w-28 !py-1 text-right`} value={ordenForm.precio_kilo ?? ""} onChange={(e) => setOrdenForm({ ...ordenForm, precio_kilo: e.target.value })} />
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-slate-400">Tolerancia (kg)</span>
                      <input type="number" min="0" className={`${INPUT} !w-28 !py-1 text-right`} value={ordenForm.peso_tolerancia ?? ""} onChange={(e) => setOrdenForm({ ...ordenForm, peso_tolerancia: e.target.value })} />
                    </div>
                    <div className="flex justify-between"><span className="text-slate-400">Exceso sobre tolerancia (kg)</span><span className="text-slate-200">{exc.toLocaleString("es-PY", { maximumFractionDigits: 3 })}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Faltante a cobrar al chofer</span><span className={falt > 0 ? "text-rose-400 font-semibold" : "text-slate-200"}>{fmtNum(falt, "PYG")}</span></div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Monto del Flete</label>
                  <input type="number" className={INPUT} value={ordenForm.monto_flete} onChange={(e) => setOrdenForm({ ...ordenForm, monto_flete: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Moneda</label>
                  <select className={INPUT} value={ordenForm.moneda} onChange={(e) => setOrdenForm({ ...ordenForm, moneda: e.target.value })}>
                    <option value="PYG">₲ Guaraníes</option>
                    <option value="USD">USD Dólares</option>
                    <option value="BRL">BRL Reales</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={LABEL}>Gastos de la carga (chofer)</label>
                  <button type="button" className="text-xs text-arandu-blue hover:underline flex items-center gap-1"
                    onClick={() => setOrdenForm({ ...ordenForm, gastos: [...(ordenForm.gastos || []), { categoria: "", descripcion: "", monto: "" }] })}>
                    <Plus className="w-3 h-3" /> Agregar gasto
                  </button>
                </div>
                {(ordenForm.gastos || []).length === 0 && <p className="text-xs text-slate-500">Sin gastos. Usá “Agregar gasto” para elegir la categoría y escribir el monto.</p>}
                <div className="space-y-2">
                  {(ordenForm.gastos || []).map((g, i) => {
                    const setG = (k, v) => setOrdenForm({ ...ordenForm, gastos: ordenForm.gastos.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
                    const opciones = g.categoria && !categoriasGasto.includes(g.categoria) ? [g.categoria, ...categoriasGasto] : categoriasGasto;
                    return (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <select className={`${INPUT} col-span-4`} value={g.categoria || ""} onChange={(e) => setG("categoria", e.target.value)}>
                          <option value="">Categoría…</option>
                          {opciones.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input className={`${INPUT} col-span-4`} value={g.descripcion || ""} onChange={(e) => setG("descripcion", e.target.value)} placeholder="Detalle (peaje, combustible…)" />
                        <input type="number" min="0" className={`${INPUT} col-span-3`} value={g.monto ?? ""} onChange={(e) => setG("monto", e.target.value)} placeholder="Monto" />
                        <button type="button" className="col-span-1 p-1 text-red-400 hover:text-red-300" onClick={() => setOrdenForm({ ...ordenForm, gastos: ordenForm.gastos.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
                {categoriasGasto.length === 0 && <p className="text-xs text-amber-400 mt-1">No hay categorías de gastos cargadas: se crean en Egresos &gt; Categorías de gastos.</p>}
              </div>
              {(() => {
                const ch = choferes.find(c => c.id === ordenForm.chofer_id);
                const flete = Number(ordenForm.monto_flete) || 0;
                const com = !ch ? 0 : ch.tipo_comision_chofer === "pct" ? Math.round(flete * (Number(ch.valor_comision_chofer) || 0) / 100 * 100) / 100 : ch.tipo_comision_chofer === "monto" ? Number(ch.valor_comision_chofer) || 0 : 0;
                const gas = (ordenForm.gastos || []).reduce((a, g) => a + (Number(g.monto) || 0), 0);
                const m = ordenForm.moneda || "PYG";
                const difP = ordenForm.peso_salida !== "" && ordenForm.peso_llegada !== "" && ordenForm.peso_salida != null && ordenForm.peso_llegada != null ? Number(ordenForm.peso_salida) - Number(ordenForm.peso_llegada) : 0;
                const excP = Math.max(difP - (Number(ordenForm.peso_tolerancia) || 0), 0);
                const faltGs = excP > 0 ? Math.round(excP * (Number(ordenForm.precio_kilo) || 0)) : 0;
                return (
                  <div className="text-xs bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2 space-y-0.5">
                    <div className="flex justify-between"><span className="text-slate-400">Flete total</span><span className="text-slate-200">{fmtNum(flete, m)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">− Comisión del chofer (sobre el total)</span><span className="text-slate-200">{fmtNum(com, m)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">− Gastos de la carga</span><span className="text-slate-200">{fmtNum(gas, m)}</span></div>
                    <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><span className="text-emerald-400 font-medium">Saldo líquido del flete</span><span className="text-emerald-400 font-semibold">{fmtNum(flete - com - gas, m)}</span></div>
                    {faltGs > 0 && <div className="flex justify-between"><span className="text-slate-400">Comisión neta al chofer (comisión − faltante)</span><span className="text-slate-200">{fmtNum(com - faltGs, m)}</span></div>}
                  </div>
                );
              })()}
              <div>
                <label className={LABEL}>Observaciones</label>
                <textarea className={INPUT} rows={2} value={ordenForm.observaciones || ""} onChange={(e) => setOrdenForm({ ...ordenForm, observaciones: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setModalOrden(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={guardarOrden} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
